import { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  RefreshCw, 
  Trash2, 
  Film,
  ImagePlus,
  Link2,
  Check,
  X,
  Play
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function VideoBlockEditor({ block, onUpdate, style }) {
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const [urlInput, setUrlInput] = useState(block.content.url || '');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleVideoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video must be under 100MB');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/api/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        onUpdate({
          ...block,
          content: { ...block.content, url: data.url, sourceType: 'upload' }
        });
        toast.success('Video uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload video');
      }
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('video/')) {
      handleVideoUpload(file);
    } else if (file?.type.startsWith('image/')) {
      handleThumbnailUpload(file);
    }
  };

  const handleThumbnailUpload = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Thumbnail must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      onUpdate({
        ...block,
        content: { ...block.content, thumbnailUrl: e.target.result, thumbnailType: 'custom' }
      });
    };
    reader.readAsDataURL(file);
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onUpdate({ ...block, content: { ...block.content, url: urlInput.trim(), sourceType: 'url' } });
      setShowUrlInput(false);
    }
  };

  const handleRemoveVideo = () => {
    onUpdate({
      ...block,
      content: { ...block.content, url: '', sourceType: '' }
    });
    setUrlInput('');
  };

  const hasVideo = block.content.url;
  const hasThumbnail = block.content.thumbnailUrl;

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">Video</label>
        
        {hasVideo ? (
          <div className="relative group rounded-lg overflow-hidden bg-black">
            <video 
              src={block.content.url} 
              controls 
              className="w-full aspect-video object-contain"
              poster={block.content.thumbnailUrl || undefined}
            />
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-all flex gap-2">
              <Button size="sm" variant="secondary" className="bg-white/90 hover:bg-white" onClick={() => videoInputRef.current?.click()}>
                <RefreshCw className="w-4 h-4 mr-1" /> Replace
              </Button>
              <Button size="sm" variant="destructive" onClick={handleRemoveVideo}>
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          </div>
        ) : (
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-200 cursor-pointer ${
              dragOver 
                ? 'border-[#B89B5E] bg-[#B89B5E]/10' 
                : 'border-gray-300 hover:border-[#B89B5E]/60 hover:bg-gray-50'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => videoInputRef.current?.click()}
          >
            <div className="flex flex-col items-center justify-center gap-3 text-center aspect-video">
              {uploading ? (
                <>
                  <div className="w-10 h-10 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-gray-500">Uploading video...</p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full bg-[#B89B5E]/10 flex items-center justify-center">
                    <Film className="w-7 h-7 text-[#B89B5E]" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Upload your video</p>
                    <p className="text-sm text-gray-500 mt-1">Drag & drop or click to upload (max 50MB)</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => handleVideoUpload(e.target.files[0])}
        />

        {!hasVideo && (
          showUrlInput ? (
            <div className="flex gap-2 mt-3">
              <Input
                placeholder="Paste video URL (YouTube, Vimeo, or direct link)"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                className="flex-1"
              />
              <Button size="sm" onClick={handleUrlSubmit} className="bg-[#B89B5E] hover:bg-[#A08050]">
                <Check className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowUrlInput(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <button
              onClick={() => setShowUrlInput(true)}
              className="w-full py-2 mt-2 text-sm text-gray-500 hover:text-[#B89B5E] transition-colors flex items-center justify-center gap-2"
            >
              <Link2 className="w-4 h-4" />
              Or paste video URL
            </button>
          )
        )}
      </div>

      <div>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">Custom Thumbnail (Optional)</label>
        {hasThumbnail ? (
          <div className="relative group rounded-lg overflow-hidden">
            <img src={block.content.thumbnailUrl} alt="Thumbnail" className="w-full aspect-video object-cover" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-3">
              <Button size="sm" variant="secondary" onClick={() => thumbnailInputRef.current?.click()}>
                <RefreshCw className="w-4 h-4 mr-2" /> Replace
              </Button>
              <Button size="sm" variant="destructive" onClick={() => onUpdate({
                ...block,
                content: { ...block.content, thumbnailUrl: '', thumbnailType: 'auto' }
              })}>
                <Trash2 className="w-4 h-4 mr-2" /> Remove
              </Button>
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                <Play className="w-8 h-8 text-[#B89B5E] ml-1" />
              </div>
            </div>
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#B89B5E]/60 hover:bg-gray-50 transition-all"
            onClick={() => thumbnailInputRef.current?.click()}
          >
            <div className="flex items-center justify-center gap-3">
              <ImagePlus className="w-5 h-5 text-gray-400" />
              <p className="text-sm text-gray-600">Upload custom thumbnail</p>
            </div>
          </div>
        )}
        <input
          ref={thumbnailInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleThumbnailUpload(e.target.files[0])}
        />
      </div>
    </div>
  );
}

export function MobileVideoBlockEditor({ block, onUpdate, style }) {
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const [urlInput, setUrlInput] = useState(block.content.url || '');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleVideoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video must be under 100MB');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/api/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        onUpdate({
          ...block,
          content: { ...block.content, url: data.url, sourceType: 'upload' }
        });
        toast.success('Mobile video uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload video');
      }
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('video/')) {
      handleVideoUpload(file);
    } else if (file?.type.startsWith('image/')) {
      handleThumbnailUpload(file);
    }
  };

  const handleThumbnailUpload = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Thumbnail must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      onUpdate({
        ...block,
        content: { ...block.content, thumbnailUrl: e.target.result, thumbnailType: 'custom' }
      });
    };
    reader.readAsDataURL(file);
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onUpdate({ ...block, content: { ...block.content, url: urlInput.trim(), sourceType: 'url' } });
      setShowUrlInput(false);
    }
  };

  const handleRemoveVideo = () => {
    onUpdate({
      ...block,
      content: { ...block.content, url: '', sourceType: '' }
    });
    setUrlInput('');
  };

  const hasVideo = block.content.url;
  const hasThumbnail = block.content.thumbnailUrl;

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <p className="text-xs font-medium text-[#B89B5E] uppercase tracking-wider">Mobile Video Block</p>
        <p className="text-xs text-gray-400">Optimized for 1080 × 1920 (9:16) vertical video</p>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">Video</label>
        
        {hasVideo ? (
          <div className="relative group rounded-2xl overflow-hidden bg-black max-w-xs mx-auto">
            <video 
              src={block.content.url} 
              controls 
              className="w-full aspect-[9/16] object-cover"
              poster={block.content.thumbnailUrl || undefined}
            />
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-all flex flex-col gap-2">
              <Button size="sm" variant="secondary" className="bg-white/90 hover:bg-white" onClick={() => videoInputRef.current?.click()}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="destructive" onClick={handleRemoveVideo}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-xs">
              9:16 Mobile
            </div>
          </div>
        ) : (
          <div
            className={`relative border-2 border-dashed rounded-2xl p-6 transition-all duration-200 cursor-pointer max-w-xs mx-auto ${
              dragOver 
                ? 'border-[#B89B5E] bg-[#B89B5E]/10' 
                : 'border-gray-300 hover:border-[#B89B5E]/60 hover:bg-gray-50'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => videoInputRef.current?.click()}
          >
            <div className="flex flex-col items-center justify-center gap-3 text-center aspect-[9/16]">
              {uploading ? (
                <>
                  <div className="w-10 h-10 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-gray-500">Uploading...</p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full bg-[#B89B5E]/10 flex items-center justify-center">
                    <Play className="w-7 h-7 text-[#B89B5E]" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-700 text-sm">Upload mobile video</p>
                    <p className="text-xs text-gray-500 mt-1">9:16 vertical format</p>
                    <p className="text-xs text-gray-400 mt-1">Max 50MB</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => handleVideoUpload(e.target.files[0])}
        />

        {!hasVideo && (
          showUrlInput ? (
            <div className="flex gap-2 mt-3 max-w-xs mx-auto">
              <Input
                placeholder="Paste video URL"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                className="flex-1 text-sm"
              />
              <Button size="sm" onClick={handleUrlSubmit} className="bg-[#B89B5E] hover:bg-[#A08050]">
                <Check className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowUrlInput(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <button
              onClick={() => setShowUrlInput(true)}
              className="w-full py-2 mt-2 text-sm text-gray-500 hover:text-[#B89B5E] transition-colors flex items-center justify-center gap-2"
            >
              <Link2 className="w-4 h-4" />
              Or paste video URL
            </button>
          )
        )}
      </div>

      <div>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">Custom Thumbnail (Optional)</label>
        {hasThumbnail ? (
          <div className="relative group rounded-2xl overflow-hidden max-w-xs mx-auto">
            <img src={block.content.thumbnailUrl} alt="Thumbnail" className="w-full aspect-[9/16] object-cover" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-3">
              <Button size="sm" variant="secondary" onClick={() => thumbnailInputRef.current?.click()}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="destructive" onClick={() => onUpdate({
                ...block,
                content: { ...block.content, thumbnailUrl: '', thumbnailType: 'auto' }
              })}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                <Play className="w-8 h-8 text-[#B89B5E] ml-1" />
              </div>
            </div>
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#B89B5E]/60 hover:bg-gray-50 transition-all max-w-xs mx-auto"
            onClick={() => thumbnailInputRef.current?.click()}
          >
            <div className="flex items-center justify-center gap-3">
              <ImagePlus className="w-5 h-5 text-gray-400" />
              <p className="text-sm text-gray-600">Upload thumbnail</p>
            </div>
          </div>
        )}
        <input
          ref={thumbnailInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleThumbnailUpload(e.target.files[0])}
        />
      </div>
    </div>
  );
}
